import IconLandscape56 from '@warp-ds/icons/react/landscape-56';
import React from 'react';
import type {
    ImageSizes,
    ImageSourceOptions,
} from '../../common/lib/image-source';

const BASE_CDN_URL = 'https://images.finncdn.no/dynamic';

const DEFAULT_RESOLUTION = 480;

const FALLBACK_IMAGE = ({ className = '' }: { className?: string }) => (
    <picture className={`${className} grid s-bg-subtle`}>
        <IconLandscape56 className="m-auto s-icon-subtle" />
    </picture>
);

// See
// https://github.schibsted.io/finn/yams-rules/blob/master/src/main/groovy/no/finntech/image/rules/RuleBuilder.groovy
// for available sizes
const IMAGE_SIZES: ImageSizes = [
    [240, '240w'],
    [320, '320w'],
    [480, '480w'],
    [640, '640w'],
    [960, '960w'],
];

type AdImageOptions = ImageSourceOptions & {
    fallbackImage: FallbackImageProp;
};

type FallbackImageProp = (args: {
    className?: string;
}) => React.ReactElement | null;

const AdImageOptions = React.createContext<AdImageOptions>({
    baseCdnUrl: BASE_CDN_URL,
    defaultResolution: DEFAULT_RESOLUTION,
    fallbackImage: FALLBACK_IMAGE,
    imageSizes: IMAGE_SIZES,
});

type AdImageOptionsProviderProps = Partial<AdImageOptions> & {
    children: React.ReactNode;
};

export function AdImageOptionsProvider(props: AdImageOptionsProviderProps) {
    const {
        baseCdnUrl,
        children,
        defaultResolution,
        fallbackImage,
        imageSizes,
    } = props;

    const value = React.useMemo<AdImageOptions>(
        () => ({
            baseCdnUrl: baseCdnUrl ?? BASE_CDN_URL,
            defaultResolution: defaultResolution ?? DEFAULT_RESOLUTION,
            fallbackImage: fallbackImage ?? FALLBACK_IMAGE,
            imageSizes: imageSizes ?? IMAGE_SIZES,
        }),
        [baseCdnUrl, defaultResolution, fallbackImage, imageSizes],
    );

    return (
        <AdImageOptions.Provider value={value}>
            {children}
        </AdImageOptions.Provider>
    );
}

export function useAdImageOptions() {
    const context = React.useContext(AdImageOptions);
    if (typeof context === 'undefined') {
        throw new Error(
            'useAdImageOptions must be used within a useAdImageOptionsProvider',
        );
    }
    return context;
}
